import type { StateCertificateEntry, StateWithholdingAnswers, SubmitStateWithholdingElectionInput } from './types';

// EPN-52 state withholding elections (PR 4): pure helpers for the administrator screens. The catalog decides which fields a state takes; nothing here
// invents a field, a default or a rate. Values are shown only as the user entered them.

export const ELECTION_PERMISSIONS = {
  read: 'employeetaxelections.read',
  write: 'employeetaxelections.write',
  approve: 'employeetaxelections.approve',
} as const;

export const MAX_NOTE = 500;

/** Mirrors StateCertificateEntry.AcceptsElections on the server (it re-checks every submission). */
export function acceptsElections(entry: StateCertificateEntry): boolean {
  return (
    entry.status === 'CertificateRequired' ||
    entry.status === 'EmptyCertificateAccepted' ||
    (entry.status === 'NoCertificateRequired' && (entry.allowsExempt || entry.allowsAdditionalWithholding || entry.allowsElectedRate))
  );
}

/** Plain-language status of a state's withholding form, from the catalog only. */
export function catalogMessage(entry: StateCertificateEntry | undefined): string {
  if (!entry) return "Escochex has no withholding form information for this state, so an election can't be recorded here.";
  switch (entry.status) {
    case 'CertificateRequired':
      return "This state needs the employee's withholding election before its tax can be calculated.";
    case 'EmptyCertificateAccepted':
      return 'This state can be calculated without an election. An election is only needed if the employee claims exemptions, extra withholding or exempt status.';
    case 'NoCertificateRequired':
      return acceptsElections(entry)
        ? 'This state can be calculated without an election. Record one only if the employee chose a different rate or claims exempt status.'
        : 'This state withholds without an employee election, so there is nothing to record.';
    case 'NotApplicable':
      return 'This state has no wage income tax, so there is no withholding election.';
    case 'NotEstablished':
      return "Escochex can't record this state's withholding form yet. It is waiting for a payroll tax specialist to confirm how it works.";
  }
}

export const percent = (rate: number) => `${Math.round(rate * 10000) / 100}%`;
export const dollars = (value: number) => `$${value.toFixed(2)}`;

export const formatDate = (value: string | null | undefined) => {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
};

export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** The answers in words, one row per answer the state takes. Only values that were entered are shown. */
export function describeAnswers(entry: StateCertificateEntry | undefined, a: StateWithholdingAnswers): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  if (a.isExempt) rows.push({ label: 'Exempt from withholding', value: 'Yes' });
  if (a.filingStatusKey) rows.push({ label: 'Filing status', value: entry?.filingStatuses.find((f) => f.key === a.filingStatusKey)?.label ?? a.filingStatusKey });
  if (a.exemptions !== null) rows.push({ label: 'Exemptions', value: String(a.exemptions) });
  if (a.exemptionAmount !== null) rows.push({ label: 'Exemption amount', value: dollars(a.exemptionAmount) });
  if (a.dependents !== null) rows.push({ label: 'Dependents', value: String(a.dependents) });
  if (a.additionalWithholding !== null) rows.push({ label: 'Extra withholding each pay period', value: dollars(a.additionalWithholding) });
  if (a.electedRate !== null) rows.push({ label: 'Withholding rate chosen', value: percent(a.electedRate) });
  if (a.lowerRateElection !== null) rows.push({ label: 'Lower rate election', value: a.lowerRateElection ? 'Yes' : 'No' });
  if (rows.length === 0) rows.push({ label: 'Answers', value: 'None. The state is calculated with its standard rules.' });
  return rows;
}

export interface ElectionForm {
  effectiveFrom: string;
  backdateReason: string;
  isExempt: boolean;
  filingStatusKey: string;
  exemptions: string;
  additionalWithholding: string;
  exemptionAmount: string;
  dependents: string;
  lowerRateElection: boolean;
  electedRate: string;
}

export const EMPTY_FORM: ElectionForm = {
  effectiveFrom: '',
  backdateReason: '',
  isExempt: false,
  filingStatusKey: '',
  exemptions: '',
  additionalWithholding: '',
  exemptionAmount: '',
  dependents: '',
  lowerRateElection: false,
  electedRate: '',
};

const MONEY = /^\d{1,7}(\.\d{1,2})?$/;

/** Client-side checks before sending. The server checks everything again; its message is shown when it refuses. Returns a request or a problem.
 * `allowBackdate` is false for the employee's own form (SME-36 default: only an administrator dates an election before today). */
export function toSubmission(
  stateCode: string,
  entry: StateCertificateEntry,
  f: ElectionForm,
  today = todayIso(),
  allowBackdate = true,
): { input?: SubmitStateWithholdingElectionInput; problem?: string } {
  if (!f.effectiveFrom) return { problem: 'Enter the date the election takes effect.' };
  const backdated = f.effectiveFrom < today;
  if (backdated && !allowBackdate) return { problem: 'Your election can start today or later. Ask your payroll administrator if it must start earlier.' };
  const reason = f.backdateReason.trim();
  if (backdated && !reason) return { problem: 'Give a reason for an election that takes effect before today.' };
  if (reason.length > MAX_NOTE) return { problem: `The reason must be ${MAX_NOTE} characters or fewer.` };

  const input: SubmitStateWithholdingElectionInput = { stateCode, effectiveFrom: f.effectiveFrom, ...(backdated ? { backdateReason: reason } : {}) };
  if (entry.allowsExempt && f.isExempt) {
    input.isExempt = true;
    return { input };
  }

  if (entry.filingStatuses.length > 0) {
    if (f.filingStatusKey) input.filingStatusKey = f.filingStatusKey;
    else if (entry.status === 'CertificateRequired') return { problem: 'Choose a filing status.' };
  }
  const money = (raw: string, label: string, set: (n: number) => void): string | null => {
    if (!raw.trim()) return null;
    if (!MONEY.test(raw.trim())) return `${label} must be a dollar amount with at most two decimal places.`;
    set(Number(raw.trim()));
    return null;
  };
  if (entry.allowsExemptions && f.exemptions.trim()) {
    const n = Number(f.exemptions.trim());
    if (!/^\d{1,2}(\.\d{1,2})?$/.test(f.exemptions.trim()) || n > 99.99) return { problem: 'Exemptions must be a number from 0 to 99.99.' };
    input.exemptions = n;
  }
  if (entry.allowsAdditionalWithholding) {
    const p = money(f.additionalWithholding, 'Extra withholding', (n) => (input.additionalWithholding = n));
    if (p) return { problem: p };
  }
  if (entry.allowsExemptionAmount) {
    const p = money(f.exemptionAmount, 'Exemption amount', (n) => (input.exemptionAmount = n));
    if (p) return { problem: p };
  }
  if (entry.allowsDependents && f.dependents.trim()) {
    if (!/^\d{1,2}$/.test(f.dependents.trim())) return { problem: 'Dependents must be a whole number from 0 to 99.' };
    input.dependents = Number(f.dependents.trim());
  }
  if (entry.allowsLowerRateElection && f.lowerRateElection) input.lowerRateElection = true;
  if (entry.allowsElectedRate && f.electedRate) input.electedRate = Number(f.electedRate);
  return { input };
}

/** Words for the codes the election API refuses with; otherwise the server's own message. */
export function electionErrorMessage(err: unknown, fallback: string): string {
  const e = err as { status?: number; code?: string | null; message?: string; messageIsServerAuthored?: boolean } | null;
  if (e?.code === 'SELF_APPROVAL_NOT_ALLOWED') return 'You entered this submission, so another reviewer must review it.';
  if (e?.code === 'CONCURRENCY_CONFLICT') return 'This submission changed since you opened it. Reload and try again.';
  if (e?.code === 'STATE_ELECTION_SUBMISSION_NOT_PENDING') return 'This submission has already been reviewed or withdrawn. Reload to see the latest.';
  if (e?.code === 'STATE_ELECTION_SUBMISSION_OPEN') return 'A submission for this state is already waiting for review. Withdraw it first to make a change.';
  if (e?.status === 403) return "Your role can't do this. Ask an administrator for access.";
  if (e?.status === 404) return 'This employee could not be found.';
  if (e?.messageIsServerAuthored && e.message && (e.status === 400 || e.status === 409)) return e.message;
  return fallback;
}
